<template>
  <div
    id="app"
    class="mui-app"
    v-bind:class="classNames">
    <div class="mui-layout__main">
      <router-view ref="main"></router-view>
    </div>
    <div class="layout__views" v-if="!!$store.state.User.uid">
      <Drawer></Drawer>
      <transition name='dialog'>
        <component
          class='mui-layout__temporary-views'
          v-bind:is="temporaryView.name"
          v-bind="temporaryView.props"
          v-if="temporaryView"></component>
      </transition>
      <MUISnackbar></MUISnackbar>
      <CMObfuscator :backdrop="temporaryView.props && temporaryView.props.backdrop" v-on:close="handleBackdropClose"></CMObfuscator>
    </div>
  </div>
</template>

<script>
import {
  CMToolbar,
  CMButton,
  CMObfuscator,
  Drawer,
  MUISnackbar,
  Navlink } from './components'

import {
  AddDialog,
  AddToCollectionDialog,
  ConfirmDialog,
  CollectionEditDialog,
  CollectionNewDialog,
  Feedback,
  HelpDialog,
  ImageDialog,
  RecipeEditDialog,
  RecipeNewDialog,
  ScaleDialog,
  PreferencesDialog,
  Credits
} from './views'

import {
  // CLEAR_COLLECTIONS,
  // CLEAR_RECIPES,
  DEREGISTER_SHORTCUT,
  REGISTER_SHORTCUT,
  // RESET_USER,
  SET_CLAIMS,
  SET_DRAWER_VISIBILITY,
  SET_USER,
  SET_WINDOW_SIZE
} from './modules/mutation-types'

import firebase from '@firebase/app'
import '@firebase/auth'
import * as Sentry from '@sentry/vue'

export default {
  name: 'App',
  components: {
    CMToolbar,
    CMButton,
    CMObfuscator,
    Drawer,
    MUISnackbar,
    Navlink,
    AddDialog,
    AddToCollectionDialog,
    ConfirmDialog,
    CollectionNewDialog,
    CollectionEditDialog,
    Feedback,
    HelpDialog,
    ImageDialog,
    RecipeEditDialog,
    RecipeNewDialog,
    ScaleDialog,
    PreferencesDialog,
    Credits
  },
  computed: {
    authenticated () {
      return !!this.$store.state.User.uid
    },
    classNames () {
      let obj = {}
      let state = this.$store.state
      // device__pixel-ratio--1
      // device__viewport-[width|height]-- # viewport
      // device__window-[width|height]--   # window, includes scroll bars does not include entire chrome
      // device__screen-[width|height]--   # screen
      // device__document-[width|height]-- # document
      obj[`app__view--${this.$router.app.$route.meta.view || 'unknown'}`] = true
      obj[`app__drawer--${state.app.drawer.visibility}`] = true
      obj[`device__pixel-ratio--${state.device.pixelRatio}`] = true
      obj[`device__viewport-class--${state.device.sizeClass}`] = true
      obj[`device__viewport-orientation--${state.device.orientation}`] = true
      obj[`device__viewport-size--${state.device.sizeName}`] = true
      obj['device__platform--ios'] = !!navigator.platform.match(/iPhone|iPod|iPad/)
      obj[`device__mode--${state.device.mode}`] = true
      obj[`device__browser--${state.device.browser.browser}`.toLowerCase()] = true
      obj[`user__clad--${state.claims && state.claims.admin}`] = true
      return obj
    },
    hasTemporaryDrawer () {
      let state = this.$store.state
      return state.device.sizeClass === 'compact' &&
      state.app.drawer.visibility === 'visible'
    },
    temporaryView () {
      return this.$store.state.app.temporaryView || {}
    }
  },
  beforeMount () {
    firebase.auth().onAuthStateChanged(user => {
      if (user) {
        this.$store.commit(SET_USER, {
          email: user.email,
          uid: user.uid
        })
        let key = user.uid
        user.getIdToken().then(token => {
          const base64Url = token.split('.')[1]
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
          const payload = JSON.parse(window.atob(base64))
          if (payload.admin) {
            let claims = Object.assign({}, payload)
            const excludeKeys = ['aud', 'auth_time', 'email', 'exp', 'firebase', 'iat', 'iss', 'sub', 'user_id']
            excludeKeys.forEach(key => {
              delete claims[key]
            })
            this.$store.commit(SET_CLAIMS, claims)
            window.analytics.identify(user.uid, claims)
          }
        }).catch((error) => {
          console.log(error)
        })
        // TODO:
        // - Some of these should fire before interface is active
        // - Use Promise.all()
        this.$store.dispatch('fetchPreferences', {userId: key})
        this.$store.dispatch('fetchRecipes', {key})
        this.$store.dispatch('fetchCollections', {userId: key})
        this.$store.dispatch('attachRecipeListeners', {key})
        this.$store.dispatch('attachCollectionListeners', {key})

        window.analytics.identify(user.uid, {
          email: user.email,
          userId: user.uid
        })
        Sentry.setUser({
          email: user.email,
          id: user.uid,
          key: user.uid
        })
      } else {
        Sentry.configureScope(scope => scope.setUser(null))
      }
    })
  },
  data () {
    return {
      dragging: false,
      start: { x: 0, y: 0 },
      shortcuts: [{
        name: 'show-library',
        keycode: 49, // 1
        altKey: true,
        fn: () => this.$router.push('/')
      }, {
        name: 'show-favorites',
        keycode: 50, // 2
        altKey: true,
        fn: () => this.$router.push('/favorites')
      }, {
        name: 'show-uncategorized',
        keycode: 51, // 3
        altKey: true,
        fn: () => this.$router.push('/uncategorized')
      }, {
        name: 'show-shortcuts',
        keycode: 52, // 4
        altKey: true,
        fn: () => console.log(JSON.parse(JSON.stringify(this.$store.state.app.shortcuts)))
      }]
    }
  },
  methods: {
    handleKeydown (ev) {
      let shortcuts, shortcut, keycode
      const app = this.$store.state.app
      keycode = ((typeof ev.keyCode !== 'undefined' && ev.keyCode) ? ev.keyCode : ev.which)
      shortcuts = Object.values(app.shortcuts)
      shortcut = shortcuts.find(shortcut => {
        return shortcut.keycode === keycode &&
          (shortcut.ctrlKey || false) === ev.ctrlKey &&
          (shortcut.altKey || false) === ev.altKey
          // Add altKey, shiftKey, metaKey for more options
      })
      if (shortcut && !app.temporaryView) {
        shortcut.fn(ev)
        ev.preventDefault()
      }
    },
    handleResize (ev) {
      this.$store.commit(SET_WINDOW_SIZE, { width: ev.currentTarget.innerWidth, height: ev.currentTarget.innerHeight })
    },
    handleBackdropClose (ev) {
      if (this.hasTemporaryDrawer) {
        this.$store.commit(SET_DRAWER_VISIBILITY, { visibility: 'hidden' })
      }
    }
    // onSwipe (evt) {
    //   let temporaryView = this.$store.state.app.temporaryView || {}
    //   if (Object.keys(temporaryView).length > 0) return
    //
    //   this.$refs.main.onSwipe(evt)
    // }
  },
  mounted () {
    window.addEventListener('keydown', this.handleKeydown)
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('orientationchange', this.handleResize)

    this.shortcuts.forEach(shortcut => {
      this.$store.commit(REGISTER_SHORTCUT, shortcut)
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('keydown', this.handleKeydown)

    this.shortcuts.map(shortcut => shortcut.name).forEach(name => {
      this.$store.commit(DEREGISTER_SHORTCUT, name)
    })
  }
}
Object.filter = (obj, predicate) =>
  Object.keys(obj)
    .filter(key => predicate(obj[key]))
    .reduce((res, key) => Object.assign(res, { [key]: obj[key] }), {})
</script>

<style src="material-design-icons/iconfont/material-icons.css"></style>

<style lang="sass">
@import "./styles/variables.scss"
@import "./styles/fonts.css"
@import "./styles/forms.sass"
@import "./styles/text.sass"
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900')
html
  touch-action: manipulation
  -webkit-tap-highlight-color: transparent
  overflow: hidden
  font-family: 'Inter', sans-serif
@supports (font-variation-settings: normal)
  html
    font-family: 'Inter var', sans-serif
html, body
  height: 100%
  // background: $background
  color: $textColor
body
  margin: 0
  padding: 0
  font: $textFont
  letter-spacing: -0.004em
  font-feature-settings: 'kern' 1, 'liga' 1, 'frac' 0
  text-rendering: geometricPrecision
  -webkit-font-smoothing: antialiased
p, li
  color: $textColor
  margin: 0 0 16px 0
a, a *
  text-decoration: none
  cursor: pointer

.mui-layout__main
  background: #fff
  height: 100%
  overflow: hidden
  position: relative
  width: 100%
  transition: transform $sheetSpeed*1.2 $animationTimingFunction
  z-index: 10
  // .has-drawer &,
  .device__viewport-class--regular.app__drawer--visible &
    transform: translate3d(250px,0,0)
  .has-toolbar &
    top: $toolbarHeight

.mui-app
  width: 100vw
  height: 100vh
  position: relative
.layout__splash
  position: absolute
  top: 0
  right: 0
  bottom: 0
  left: 0
  background: #ffffff
  z-index: 1
.component-fade-enter-active
  transition: opacity .32s $animationTimingFunction, transform .32s $animationTimingFunction
.component-fade-enter, .component-fade-leave-to
  opacity: 0
  transform: translateY(4px)
.component-fade-leave
  display: none

.mui-dialog--window
  &.dialog-enter-active, &.slide__left-leave-active
    transition: top .20s $animationTimingFunction
  &.dialog-enter, &.dialog-leave-active
    top: 100%

.help.is-danger
  color: #e82110
  font-size: 14px
.ff-frac
  font-feature-settings: 'frac' 1, 'salt' 1
.form-group
  input:-webkit-autofill, textarea:-webkit-autofill, select:-webkit-autofill
    background-color: transparent!important

.mui-list-divider
  border-bottom: $layoutBorder
  width: 100%
</style>



// WEBPACK FOOTER //
// src/App.vue